import { useState, lazy, Suspense } from 'react';
import { DndContext, closestCenter, DragEndEvent } from '@dnd-kit/core';
import { arrayMove, SortableContext, rectSortingStrategy } from '@dnd-kit/sortable';

import StatCard from '../shared/StatCard';
import { PageNavigation, PageBackground } from '../shared';
import { initialWidgets, statCardData } from '../../data/mockData';
import { Widget as WidgetType } from '../../types';

import { CalendarPlus, Pencil, Check } from 'lucide-react';

// Lazy load Widget component for code splitting
const Widget = lazy(() => import('./Widget'));

const Dashboard = () => {
  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const [widgets, setWidgets] = useState<WidgetType[]>(initialWidgets);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      setWidgets((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over.id);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  const removeWidget = (id: string) => {
    setWidgets(widgets.filter(widget => widget.id !== id));
  };

  const handleWidgetSettings = (id: string) => {
    console.log('Settings for widget:', id);
    // Placeholder for settings logic
  };

  const addWidget = () => {
    const newWidget: WidgetType = {
      id: `new-${Date.now()}`,
      title: 'New Widget',
      icon: <CalendarPlus />,
      tag: 'low',
      content: <p>This is a newly added widget. You can customize its content.</p>,
      footer: <a href="#!">Configure →</a>,
    };
    setWidgets([...widgets, newWidget]);
  };

  return (
    <PageBackground>
      <main className="max-w-[1600px] mx-auto p-4 sm:p-8">
        <header className="flex justify-between items-center mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 m-0">Dashboard</h1>
          <PageNavigation
            primaryAction={{
              label: isEditMode ? 'Done' : 'Edit Dashboard',
              icon: isEditMode ? Check : Pencil,
              onClick: () => setIsEditMode(!isEditMode),
              displayStyle: isEditMode ? 'secondary' : 'primary',
              showOnMobile: true,
            }}
          />
        </header>

        <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8" aria-label="Key statistics">
          {statCardData.map((stat, index) => (
            <StatCard
              key={index}
              title={stat.title}
              value={stat.value}
              trend={stat.trend}
              change={stat.change}
              isPositive={stat.isPositive}
            />
          ))}
        </section>

        <div className="grid grid-cols-1 gap-8 items-start">
          <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={widgets.map(w => w.id)} strategy={rectSortingStrategy}>
              <section className="grid grid-cols-[repeat(auto-fit,minmax(350px,1fr))] gap-6 items-start" role="region" aria-label="Dashboard widgets">
                <Suspense fallback={<div className="flex items-center justify-center min-h-[200px] text-gray-500 text-base font-medium">Loading widgets...</div>}>
                  {widgets.map(widget => (
                    <Widget
                      key={widget.id}
                      id={widget.id}
                      icon={widget.icon}
                      title={widget.title}
                      tag={widget.tag}
                      footer={widget.footer}
                      isEditMode={isEditMode}
                      onRemove={removeWidget}
                      onSettings={handleWidgetSettings}
                      isOneAct={widget.id === 'one-act'}
                    >
                      {widget.content}
                    </Widget>
                  ))}
                </Suspense>
                {isEditMode && (
                  <button 
                    onClick={addWidget} 
                    className="flex items-center justify-center border-2 border-dashed border-gray-300 text-gray-500 bg-transparent rounded-lg min-h-[150px] text-lg font-medium cursor-pointer transition-all hover:border-primary hover:text-primary hover:bg-primary/10"
                    aria-label="Add new widget"
                  >
                    + Add Widget
                  </button>
                )}
              </section>
            </SortableContext>
          </DndContext>
        </div>
      </main>
    </PageBackground>
  );
};

export default Dashboard;
